import { Trophy } from 'lucide-react';
import { useState } from 'react';
import { Podium } from '../../components/Podium.tsx';
import type { PartenaireClasse } from '../../lib/friends.ts';

/** Les trois filtres, et l'ordre dans lequel ils se lisent. « Tous » d'abord :
 *  c'est le classement dont on parle, les deux autres le découpent. */
const MODES = [
    ['tous', 'Tous'],
    ['rounds', 'Parcours'],
    ['putting', 'Putting'],
] as const;

type Mode = (typeof MODES)[number][0];

/* Qui gagne, quand on joue ensemble.
 *
 * ── Des victoires, et non des scores ────────────────────────────────────────
 *
 * Un total de coups ne se compare pas d'un parcours à l'autre, ni un nombre de
 * putts à un écart au par. Une victoire, si : elle se compte de la même façon
 * sur un green et sur dix-huit trous, et c'est la seule chose que trois
 * joueurs autour d'une table se disputent vraiment. Le filtre change donc les
 * parties qu'on compte, jamais l'unité — c'est ce qui permet à « Tous »
 * d'exister.
 *
 * ── Vous êtes dessus ────────────────────────────────────────────────────────
 *
 * C'est le classement des parties COMMUNES : vous les avez toutes jouées, et
 * un podium de vos partenaires d'où vous seriez absent ne dirait pas qui mène,
 * seulement qui mène derrière vous.
 *
 * ── Ce que le nombre de parties fait là ─────────────────────────────────────
 *
 * Trois victoires sur trois et trois victoires sur douze ne se lisent pas
 * pareil. Le compte suit donc le score, en petit, et il départage les égalités
 * : à victoires égales, celui qui a joué moins de parties passe devant.
 */
export function PodiumPartenaires({ joueurs, moiId }: {
    joueurs: PartenaireClasse[];
    moiId: string | null | undefined;
}) {
    const [mode, setMode] = useState<Mode>('tous');

    const bilan = (j: PartenaireClasse) => (
        mode === 'rounds' ? j.rounds
            : mode === 'putting' ? j.putting
                : {
                    wins: j.rounds.wins + j.putting.wins,
                    games: j.rounds.games + j.putting.games,
                }
    );

    const classes = joueurs
        .map((j) => ({ j, ...bilan(j) }))
        .filter((l) => l.games > 0)
        /* À victoires égales, le moins de parties : gagner trois fois sur
           trois vaut mieux que gagner trois fois sur douze. Le nom en dernier
           recours, pour que l'ordre ne bouge pas d'un chargement à l'autre. */
        .sort((a, b) => b.wins - a.wins
            || a.games - b.games
            || a.j.displayName.localeCompare(b.j.displayName, 'fr'));

    const trois = classes.slice(0, 3);
    /* Tout le monde a-t-il joué le même nombre de parties ?
       Quand oui — le cas courant, on joue ensemble — « sur 3 » répété sous
       trois scores n'apprend rien et alourdit l'estrade : le nombre se dit
       une seule fois, sous le podium. Quand non, il redevient indispensable :
       deux victoires sur deux ne valent pas deux victoires sur neuf. */
    const memeNombre = new Set(trois.map((l) => l.games)).size === 1;
    const parties = trois[0]?.games ?? 0;

    return (
        <section className="rounded-box border border-base-300 bg-base-100 p-4 shadow-sm">
            <h2 className="flex items-center gap-2 text-sm font-semibold">
                <Trophy size={18} style={{ color: 'var(--brass-ink)' }} />
                Le plus souvent devant
            </h2>

            {/* Le filtre en pastilles, comme les onglets des statistiques :
                l'actif est une carte blanche posée sur le papier: un
                soulignement se lit mal au soleil et ne dit pas qu'on peut
                appuyer. */}
            <div role="tablist" className="mt-3 flex gap-1 rounded-box bg-base-200 p-1">
                {MODES.map(([cle, mot]) => (
                    <button key={cle} role="tab" type="button"
                        aria-selected={mode === cle}
                        onClick={() => setMode(cle)}
                        className={`flex-1 rounded-field py-2 text-sm font-bold transition-colors ${
                            mode === cle ? 'bg-base-100 shadow-sm' : 'opacity-60 hover:opacity-100'
                        }`}>
                        {mot}
                    </button>
                ))}
            </div>

            {classes.length < 2 ? (
                /* Un podium à un seul joueur n'est pas un podium. On le dit
                   plutôt que de faire disparaître la carte sous le doigt : un
                   écran qui se vide au tap se lit comme une panne. */
                <p className="py-6 text-center text-sm opacity-60">
                    {mode === 'rounds'
                        ? "Aucun parcours terminé à plusieurs pour l’instant."
                        : mode === 'putting'
                            ? "Aucune partie de putting terminée à plusieurs pour l’instant."
                            : "Aucune partie terminée à plusieurs pour l’instant."}
                </p>
            ) : (
                <>
                    <Podium moiId={moiId}
                        joueurs={trois.map((l) => ({
                            userId: l.j.userId,
                            displayName: l.j.displayName,
                            avatarUrl: l.j.avatarUrl,
                            valeur: String(l.wins),
                            detail: memeNombre ? null : `sur ${l.games}`,
                        }))} />

                    {/* Dire de quoi le grand chiffre est le compte. Ailleurs
                        dans l'application, un podium porte des coups — le plus
                        petit gagne ; ici il porte des victoires, et le plus
                        grand gagne. Sans cette ligne, on lirait l'inverse. */}
                    <p className="mt-1 text-center text-xs opacity-60">
                        {memeNombre
                            ? `Victoires sur vos ${parties} partie${parties > 1 ? 's' : ''} jouée${parties > 1 ? 's' : ''} ensemble`
                            : 'Victoires sur les parties jouées ensemble'}
                    </p>

                    {/* Au-delà des marches, la suite en une ligne : à cinq
                        partenaires, disparaître du classement parce qu'on est
                        quatrième est plus vexant qu'utile. */}
                    {classes.length > 3 && (
                        <ul className="mt-3 space-y-1 border-t border-base-300 pt-3">
                            {classes.slice(3).map((l, i) => (
                                <li key={l.j.userId}
                                    className="flex items-baseline gap-2 text-sm">
                                    <span className="w-5 shrink-0 text-center font-bold tabular-nums opacity-60">
                                        {i + 4}
                                    </span>
                                    <span className={`min-w-0 flex-1 truncate ${
                                        l.j.userId === moiId ? 'font-bold' : 'font-semibold opacity-80'}`}>
                                        {l.j.userId === moiId ? 'vous' : l.j.displayName}
                                    </span>
                                    <span className="font-score shrink-0 tabular-nums">
                                        {l.wins}
                                        <span className="ml-1 text-xs font-normal opacity-60">
                                            sur {l.games}
                                        </span>
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </>
            )}
        </section>
    );
}
